import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Link, useNavigate } from 'react-router-dom';
import './Announcements.css';

function AllAnnouncements() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem('jwtToken');

  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);

  const fetchAnnouncements = useCallback(async () => {
    try {
      setLoading(true);

      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/announcements`,
        {
          params: { showInactive },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setAnnouncements(res.data.data || []);
    } catch (error) {
      console.error('Fout bij ophalen mededelingen:', error);
      setAnnouncements([]);
    } finally {
      setLoading(false);
    }
  }, [token, showInactive]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const handleDelete = async (announcement) => {
    const result = await Swal.fire({
      title: 'Mededeling verwijderen?',
      text: `Weet je zeker dat je "${announcement.title}" wilt verwijderen?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Ja, verwijderen',
      cancelButtonText: 'Annuleren',
      confirmButtonColor: '#dc3545',
    });

    if (!result.isConfirmed) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/announcements/${announcement.announcement_id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      await Swal.fire({
        toast: true,
        position: 'top',
        icon: 'success',
        title: 'Mededeling verwijderd',
        showConfirmButton: false,
        timer: 1800,
        timerProgressBar: true,
      });

      fetchAnnouncements();
    } catch (error) {
      console.error('Fout bij verwijderen mededeling:', error);

      await Swal.fire({
        toast: true,
        position: 'top',
        icon: 'error',
        title: 'Verwijderen mislukt',
        showConfirmButton: false,
        timer: 2200,
        timerProgressBar: true,
      });
    }
  };

  const formatDate = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleDateString('nl-NL');
  };

  return (
    <div className="announcements-page">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1>Mededelingenbeheer</h1>

        <Link to="/newannouncement" className="btn btn-success">
          <i className="bi bi-plus-circle me-2"></i>
          Nieuwe mededeling
        </Link>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="form-check">
          <input
            id="showInactiveAnnouncements"
            className="form-check-input"
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="showInactiveAnnouncements">
            Toon ook inactieve mededelingen
          </label>
        </div>
      </div>

      {loading ? (
        <p>Mededelingen laden...</p>
      ) : announcements.length === 0 ? (
        <p>Geen mededelingen gevonden.</p>
      ) : (
        <div className="announcement-cards">
          {announcements.map((item) => (
            <div
              key={item.announcement_id}
              className="announcement-card"
              onClick={() => navigate(`/editannouncement/${item.announcement_id}`)}
            >
              <div className="announcement-card-header">
                <div>
                  <div className="announcement-card-title">{item.title}</div>
                  <div className="announcement-card-meta">
                    {item.is_general ? 'Algemeen voor iedereen' : 'Gekoppeld aan groepen'}
                  </div>
                </div>

                <span
                  className={`badge ${
                    item.status === 'active' ? 'bg-success' : 'bg-secondary'
                  }`}
                >
                  {item.status}
                </span>
              </div>

              {item.body && (
                <p className="announcement-card-body">
                  {item.body.length > 120 ? `${item.body.slice(0, 120)}...` : item.body}
                </p>
              )}

              <div className="announcement-card-info">
                <span>
                  <i className="bi bi-calendar-event me-1"></i>
                  Vanaf: {formatDate(item.visible_from)}
                </span>
                <span>
                  <i className="bi bi-calendar-x me-1"></i>
                  T/m: {formatDate(item.visible_until)}
                </span>
              </div>

              {Array.isArray(item.groups) && item.groups.length > 0 && (
                <div className="announcement-groups-preview">
                  {item.groups.map((group) => (
                    <span key={group.groep_id} className="announcement-group-pill">
                      {group.groep_name}
                    </span>
                  ))}
                </div>
              )}

              <div className="announcement-card-actions" onClick={(e) => e.stopPropagation()}>
                <Link
                  to={`/editannouncement/${item.announcement_id}`}
                  className="btn btn-sm btn-primary"
                >
                  Bewerken
                </Link>

                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleDelete(item)}
                >
                  Verwijderen
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AllAnnouncements;